import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useRef, useState, RefObject } from 'react'

import { useDebouncedDebugLog } from './use_debounced_debug_log'

const isBrowserEnvironment = (): boolean => typeof window !== 'undefined'

/** CSS var published by useStickyHeaderScrollOffset; equals scroll-to-date's scroll-margin-top. */
const SCROLL_OFFSET_VAR = '--meeting-group-scroll-offset'
/** Fallback when the sticky-header offset var is unset (matches MeetingGroup's static fallback). */
const DEFAULT_ANCHOR_OFFSET_PX = 164
/** Slack so rounding right after a jump (group top ≈ offset ± 1px) still selects that group. */
const ANCHOR_SLACK_PX = 12

/** Viewport Y where scroll-to-date parks a date group's top (sticky header bottom + gap). */
function getAnchorOffsetPx(): number {
  if (!isBrowserEnvironment()) return DEFAULT_ANCHOR_OFFSET_PX
  const raw = getComputedStyle(document.documentElement).getPropertyValue(SCROLL_OFFSET_VAR)
  const parsed = parseFloat(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_ANCHOR_OFFSET_PX
}

/**
 * Date group that currently owns the sticky-header anchor line — the same line that scroll-to-date
 * aligns a group's top to via `scroll-margin-top`. Selecting the group whose top sits at/just above
 * that line (rather than the one nearest the viewport center) keeps the picker label in sync with
 * the jump target, so picking Jun 20 stays on Jun 20 instead of snapping to Jun 21.
 */
function computeAnchorDate(dateGroupRefs: Map<string, HTMLElement>): string | null {
  if (!isBrowserEnvironment() || dateGroupRefs.size === 0) return null

  const anchorY = getAnchorOffsetPx() + ANCHOR_SLACK_PX
  let activeDate: string | null = null
  let activeTop = -Infinity
  let firstDate: string | null = null
  let firstTop = Infinity

  dateGroupRefs.forEach((element, rawDate) => {
    const rect = element.getBoundingClientRect()
    if (rect.height === 0) return
    const { top } = rect
    if (top < firstTop) {
      firstTop = top
      firstDate = rawDate
    }
    // Lowest group whose top has not yet scrolled above the anchor line.
    if (top <= anchorY && top > activeTop) {
      activeTop = top
      activeDate = rawDate
    }
  })

  // Above the first group (list scrolled to the very top), fall back to the topmost group.
  return activeDate ?? firstDate
}

/**
 * Hook to track visible date groups using IntersectionObserver.
 * Used for hybrid polling strategy (first page + visible meetings).
 * Tracks date groups rather than individual meetings for simplicity.
 *
 * @param containerRef - Ref to the scrollable container
 * @param meetings - Array of meeting groups with rawDate
 * @param bufferPx - Buffer zone in pixels (default: 500px above/below viewport)
 * @param trackCenterDate - When true, tracks scroll position and returns the date group closest to viewport center
 * @returns Object with visibleDateGroups (Set of rawDate strings), dateRange (min/max dates), registerDateGroupRef, and centerDate
 */
export function useVisibleDateGroups(
  containerRef: RefObject<HTMLElement>,
  meetings: Array<{ rawDate?: string; events?: unknown[] }>,
  bufferPx: number = 500,
  trackCenterDate: boolean = false
): {
  visibleDateGroups: Set<string>
  dateRange: { start: string | null; end: string | null }
  centerDate: string | null
  registerDateGroupRef: (rawDate: string, element: HTMLElement | null) => void
} {
  const [visibleDateGroups, setVisibleDateGroups] = useState<Set<string>>(new Set())
  const [centerDate, setCenterDate] = useState<string | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const dateGroupRefsRef = useRef<Map<string, HTMLElement>>(new Map())
  const allDateGroupsRef = useRef<string[]>([])
  const trackCenterDateRef = useRef(trackCenterDate)
  trackCenterDateRef.current = trackCenterDate

  const debouncedDebugLog = useDebouncedDebugLog()

  const allDateGroups = useMemo(
    () => (Array.isArray(meetings) ? meetings : [])
      .map((group) => group.rawDate)
      .filter((date): date is string => !!date),
    [meetings]
  )
  allDateGroupsRef.current = allDateGroups

  const dateRange = useMemo(() => {
    if (visibleDateGroups.size === 0) {
      debouncedDebugLog('[Visible Range Debug] No visible date groups', {})
      return { start: null, end: null }
    }

    const visibleDates = Array.from(visibleDateGroups)
      .map((dateStr) => moment(dateStr))
      .filter((date) => date.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf())

    if (visibleDates.length === 0) {
      debouncedDebugLog('[Visible Range Debug] No valid visible dates after filtering', {})
      return { start: null, end: null }
    }

    const result = {
      start: visibleDates[0].clone().startOf('day').toISOString(),
      end: visibleDates[visibleDates.length - 1].clone().endOf('day').toISOString(),
    }

    debouncedDebugLog('[Visible Range Debug] Date range calculated:', {
      visibleDateGroupsCount: visibleDateGroups.size,
      visibleDates: Array.from(visibleDateGroups).sort(),
      dateRange: result,
      rangeDays: moment(result.end).diff(moment(result.start), 'days'),
      allDateGroupsCount: allDateGroups.length,
      registeredElementsCount: dateGroupRefsRef.current.size,
    })

    return result
  }, [visibleDateGroups, allDateGroups, debouncedDebugLog])

  const updateCenterDate = useCallback(() => {
    const next = computeAnchorDate(dateGroupRefsRef.current)
    setCenterDate((prev) => (prev === next ? prev : next))
  }, [])

  useEffect(() => {
    if (!trackCenterDate || !isBrowserEnvironment()) return undefined

    let rafId: number | null = null
    const handleScrollOrResize = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        updateCenterDate()
      })
    }

    // Capture so scrolls inside the app-shell content container (which is what actually scrolls in
    // the product-refresh shell, not window) still update the anchored date — keeps the picker label
    // tracking smoothly instead of only updating on resize/registration.
    window.addEventListener('scroll', handleScrollOrResize, { passive: true, capture: true })
    window.addEventListener('resize', handleScrollOrResize, { passive: true })
    handleScrollOrResize()

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScrollOrResize, { capture: true })
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [trackCenterDate, updateCenterDate])

  useEffect(() => {
    if (!trackCenterDate) return
    updateCenterDate()
  }, [trackCenterDate, allDateGroups, updateCenterDate])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    const isContainerScrollable = container.scrollHeight > container.clientHeight
    const useViewportRoot = !isContainerScrollable

    debouncedDebugLog('[Visible Range Debug] IntersectionObserver setup:', {
      containerScrollHeight: container.scrollHeight,
      containerClientHeight: container.clientHeight,
      isContainerScrollable,
      useViewportRoot,
      bufferPx,
    })

    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibleDateGroups((prev) => {
          const next = new Set(prev)
          entries.forEach((entry) => {
            const rawDate = entry.target.getAttribute('data-date')
            if (rawDate) {
              if (entry.isIntersecting) {
                next.add(rawDate)
              } else {
                next.delete(rawDate)
              }
            }
          })

          // Return prev when contents are unchanged so React skips re-render (reference equality).
          if (next.size === prev.size && [...next].every((d) => prev.has(d))) {
            return prev
          }

          debouncedDebugLog('[Visible Range Debug] IntersectionObserver callback:', {
            entriesCount: entries.length,
            prevSize: prev.size,
            nextSize: next.size,
            added: Array.from(next).filter((d) => !prev.has(d)),
            removed: Array.from(prev).filter((d) => !next.has(d)),
          })

          return next
        })
      },
      {
        root: useViewportRoot ? null : container,
        rootMargin: `${bufferPx}px`,
        threshold: 0,
      }
    )

    dateGroupRefsRef.current.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element)
      }
    })

    debouncedDebugLog('[Visible Range Debug] IntersectionObserver setup complete:', {
      containerExists: !!container,
      bufferPx,
      totalElementsRegistered: dateGroupRefsRef.current.size,
      allDateGroups,
      useViewportRoot,
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [containerRef, bufferPx, debouncedDebugLog, allDateGroups])

  useEffect(() => {
    if (!observerRef.current) return

    dateGroupRefsRef.current.forEach((element, rawDate) => {
      if (allDateGroups.includes(rawDate)) {
        observerRef.current?.observe(element)
      }
    })
  }, [allDateGroups])

  useEffect(() => {
    const datesToRemove: string[] = []
    dateGroupRefsRef.current.forEach((element, rawDate) => {
      if (!allDateGroups.includes(rawDate)) {
        datesToRemove.push(rawDate)
        dateGroupRefsRef.current.delete(rawDate)
        if (observerRef.current) {
          observerRef.current.unobserve(element)
        }
      }
    })
    if (datesToRemove.length > 0) {
      setVisibleDateGroups((prev) => {
        const next = new Set(prev)
        datesToRemove.forEach((d) => next.delete(d))
        if (next.size === prev.size && [...next].every((d) => prev.has(d))) {
          return prev
        }
        return next
      })
    }
  }, [allDateGroups])

  const registerDateGroupRef = useCallback((rawDate: string, element: HTMLElement | null) => {
    const currentDateGroups = allDateGroupsRef.current
    if (element) {
      element.setAttribute('data-date', rawDate)
      dateGroupRefsRef.current.set(rawDate, element)
      if (observerRef.current && currentDateGroups.includes(rawDate)) {
        observerRef.current.observe(element)
        debouncedDebugLog('[Visible Range Debug] Registered date group:', {
          rawDate,
          elementExists: !!element,
          isObserved: true,
          totalRegistered: dateGroupRefsRef.current.size,
          allDateGroupsCount: currentDateGroups.length,
        })
      }
    } else {
      const existingElement = dateGroupRefsRef.current.get(rawDate)
      if (existingElement && observerRef.current) {
        observerRef.current.unobserve(existingElement)
      }
      dateGroupRefsRef.current.delete(rawDate)
      debouncedDebugLog('[Visible Range Debug] Unregistered date group:', {
        rawDate,
        totalRegistered: dateGroupRefsRef.current.size,
      })
    }

    if (trackCenterDateRef.current) {
      updateCenterDate()
    }
  }, [debouncedDebugLog, updateCenterDate])

  return { visibleDateGroups, dateRange, centerDate, registerDateGroupRef }
}
